import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertTriangle,
  Wrench,
  Loader2,
  RefreshCw,
  Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Stat Card Component - defined outside main component
function StatCard({ title, value, subtitle, icon: Icon, color = "blue" }) {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-600",
    emerald: "bg-emerald-100 text-emerald-600",
    amber: "bg-amber-100 text-amber-600",
    red: "bg-red-100 text-red-600"
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 font-medium">{title}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
          {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color] || colorClasses.blue}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30");
  const [metrics, setMetrics] = useState({
    totalRequests: 0,
    completedRequests: 0,
    avgResponseTime: 0,
    openRequests: 0,
    overdueRequests: 0,
    requestsByPriority: [],
    requestsByType: [],
    requestsByStage: [],
    monthlyTrend: [],
    topContractors: []
  });

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const pipelineRes = await fetch(`${API}/maintenance/pipeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      let pipelineData = {};
      let allRequests = [];

      if (pipelineRes.ok) {
        const data = await pipelineRes.json();
        pipelineData = data.pipeline || {};
        
        // Flatten all items from pipeline
        Object.values(pipelineData).forEach(items => {
          if (Array.isArray(items)) allRequests.push(...items);
        });
      }

      const now = new Date();
      const daysAgo = new Date(now - parseInt(dateRange) * 24 * 60 * 60 * 1000);

      const filteredRequests = allRequests.filter(r => {
        const created = new Date(r.created_at);
        return created >= daysAgo;
      });

      // Count by stage
      const requestsByStage = [
        { name: "Intake", value: pipelineData.intake?.length || 0, color: "#64748b" },
        { name: "Triage", value: pipelineData.triage?.length || 0, color: "#f59e0b" },
        { name: "Dispatch", value: pipelineData.dispatch?.length || 0, color: "#3b82f6" },
        { name: "In Progress", value: pipelineData.in_progress?.length || 0, color: "#8b5cf6" },
        { name: "Completion", value: pipelineData.completion?.length || 0, color: "#10b981" },
        { name: "Closed", value: pipelineData.closed?.length || 0, color: "#374151" }
      ];

      // Count by priority
      const priorityCounts = { low: 0, medium: 0, high: 0, critical: 0 };
      filteredRequests.forEach(r => {
        const p = (r.priority || "medium").toLowerCase();
        if (priorityCounts[p] !== undefined) priorityCounts[p]++;
      });

      const requestsByPriority = [
        { name: "Critical", value: priorityCounts.critical, color: "#ef4444" },
        { name: "High", value: priorityCounts.high, color: "#f97316" },
        { name: "Medium", value: priorityCounts.medium, color: "#eab308" },
        { name: "Low", value: priorityCounts.low, color: "#22c55e" }
      ];

      // Count by type
      const typeCounts = {};
      filteredRequests.forEach(r => {
        const type = r.type || r.request_type || "other";
        typeCounts[type] = (typeCounts[type] || 0) + 1;
      });

      const requestsByType = Object.entries(typeCounts)
        .map(([name, value]) => ({ 
          name: name.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()), 
          value 
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);

      // Calculate response time
      let totalResponseTime = 0;
      let responseCount = 0;
      filteredRequests.forEach(r => {
        if (r.activity && r.activity.length > 1) {
          const created = new Date(r.created_at);
          const firstAction = new Date(r.activity[1]?.timestamp || r.updated_at);
          const hours = (firstAction - created) / (1000 * 60 * 60);
          if (hours > 0 && hours < 720) {
            totalResponseTime += hours;
            responseCount++;
          }
        }
      });

      // Top contractors
      const contractorStats = {};
      allRequests.forEach(r => {
        if (r.assigned_contractor_id && r.stage === "closed") {
          const cid = r.assigned_contractor_id;
          if (!contractorStats[cid]) {
            contractorStats[cid] = {
              id: cid,
              name: r.assigned_contractor_name || "Unknown",
              completed: 0
            };
          }
          contractorStats[cid].completed++;
        }
      });

      const topContractors = Object.values(contractorStats)
        .sort((a, b) => b.completed - a.completed)
        .slice(0, 5);

      // Monthly trend
      const monthlyData = {};
      for (let i = 5; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        monthlyData[key] = { month: key, created: 0, completed: 0 };
      }

      allRequests.forEach(r => {
        const created = new Date(r.created_at);
        const key = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}`;
        if (monthlyData[key]) {
          monthlyData[key].created++;
          if (r.stage === "closed") monthlyData[key].completed++;
        }
      });

      const openCount = allRequests.filter(r => r.stage !== "closed").length;

      setMetrics({
        totalRequests: filteredRequests.length,
        completedRequests: filteredRequests.filter(r => r.stage === "closed").length,
        avgResponseTime: responseCount > 0 ? (totalResponseTime / responseCount).toFixed(1) : 0,
        openRequests: openCount,
        overdueRequests: filteredRequests.filter(r => {
          const created = new Date(r.created_at);
          const age = (now - created) / (1000 * 60 * 60 * 24);
          return r.stage !== "closed" && age > 7;
        }).length,
        requestsByPriority,
        requestsByType,
        requestsByStage,
        monthlyTrend: Object.values(monthlyData),
        topContractors
      });

    } catch (err) {
      console.error("Failed to fetch analytics:", err);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  const totalInPipeline = metrics.requestsByStage.reduce((sum, s) => sum + s.value, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-blue-600" />
            Analytics Dashboard
          </h2>
          <p className="text-slate-500">Performance metrics and insights</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Total Requests" 
          value={metrics.totalRequests}
          subtitle={`${dateRange} day period`}
          icon={Wrench}
          color="blue"
        />
        <StatCard 
          title="Completed" 
          value={metrics.completedRequests}
          subtitle={`${((metrics.completedRequests / metrics.totalRequests) * 100 || 0).toFixed(0)}% completion rate`}
          icon={CheckCircle}
          color="emerald"
        />
        <StatCard 
          title="Avg Response Time" 
          value={`${metrics.avgResponseTime}h`}
          subtitle="Time to first action"
          icon={Clock}
          color="amber"
        />
        <StatCard 
          title="Open Requests" 
          value={metrics.openRequests}
          subtitle={`${metrics.overdueRequests} overdue (>7 days)`}
          icon={AlertTriangle}
          color="red"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Pipeline Distribution</h3>
          <div className="space-y-3">
            {metrics.requestsByStage.map(stage => (
              <div key={stage.name} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{stage.name}</span>
                  <span className="font-medium text-slate-900">{stage.value}</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all"
                    style={{ 
                      width: `${totalInPipeline > 0 ? (stage.value / totalInPipeline) * 100 : 0}%`,
                      backgroundColor: stage.color
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Priority Breakdown */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-4">By Priority</h3>
          <div className="space-y-3">
            {metrics.requestsByPriority.map(item => (
              <div key={item.name} className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="flex-1 text-slate-600">{item.name}</span>
                <span className="font-semibold text-slate-900">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Request Types & Top Contractors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Request Types */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Top Request Types</h3>
          {metrics.requestsByType.length > 0 ? (
            <div className="space-y-3">
              {metrics.requestsByType.map((type, idx) => (
                <div key={type.name} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                    {idx + 1}
                  </span>
                  <span className="flex-1 text-slate-600">{type.name}</span>
                  <span className="font-semibold text-slate-900">{type.value}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-sm italic">No data available</p>
          )}
        </div>

        {/* Top Contractors */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Top Contractors
          </h3>
          {metrics.topContractors.length > 0 ? (
            <div className="space-y-3">
              {metrics.topContractors.map((contractor, idx) => (
                <div key={contractor.id} className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-amber-100 text-amber-600' : 
                    idx === 1 ? 'bg-slate-200 text-slate-600' :
                    idx === 2 ? 'bg-orange-100 text-orange-600' :
                    'bg-slate-100 text-slate-500'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="flex-1 text-slate-600">{contractor.name}</span>
                  <span className="font-semibold text-slate-900">{contractor.completed} completed</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-sm italic">No contractor data yet</p>
          )}
        </div>
      </div>

      {/* Monthly Trend */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-900 mb-4">6-Month Trend</h3>
        <div className="flex items-end gap-2 h-40">
          {metrics.monthlyTrend.map((month) => {
            const maxVal = Math.max(...metrics.monthlyTrend.map(m => m.created), 1);
            const createdHeight = (month.created / maxVal) * 100;
            const completedHeight = (month.completed / maxVal) * 100;
            
            return (
              <div key={month.month} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex gap-1 items-end h-32">
                  <div 
                    className="flex-1 bg-blue-200 rounded-t transition-all"
                    style={{ height: `${createdHeight}%` }}
                    title={`Created: ${month.created}`}
                  />
                  <div 
                    className="flex-1 bg-emerald-400 rounded-t transition-all"
                    style={{ height: `${completedHeight}%` }}
                    title={`Completed: ${month.completed}`}
                  />
                </div>
                <span className="text-xs text-slate-500">
                  {month.month.split('-')[1]}
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex justify-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-200 rounded" />
            <span className="text-slate-600">Created</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-emerald-400 rounded" />
            <span className="text-slate-600">Completed</span>
          </div>
        </div>
      </div>
    </div>
  );
}
